/** @jsx isolateComponent */
import { css } from '@emotion/core';
import { Animation, ColorName, Colors, ColorValue } from '../../../theme';
import { isolateComponent, labelStyles } from '@zapier/style-encapsulation';
import { clamp } from '../../../utils/clamp';
import { useTheme } from '../../../index';

type Props = {
  /**
   * The color of the progress bar.
   * @default 'ocean'
   */
  color?: ColorName;
  /**
   * The maximum value for the progress bar.
   * @default 1
   */
  max?: number;
  /**
   * The minimum value for the progress bar.
   * @default 0
   */
  min?: number;
  /**
   * The amount of the progress bar. Should be between `min` and `max`.
   */
  value: number;
};

const mapV4ToV5Color = (color: ColorValue): ColorValue => {
  const colorMap = {
    [Colors.ocean]: Colors.blue,
    [Colors.warning]: Colors.warning400,
    [Colors.error]: Colors.error400,
  };

  type keys = keyof typeof colorMap;

  return colorMap[color as keys] || color;
};

const V5Overrides = {
  progress: (color: ColorName) => [
    css`
      background-color: ${mapV4ToV5Color(Colors[color])};
    `,
  ],
};

const Styles = labelStyles('ProgressBar', {
  root: (isRebrand: boolean) =>
    css({
      position: 'relative',
      height: 5,
      borderRadius: 2.5,
      backgroundColor: isRebrand ? Colors.neutral300 : Colors.gray20,
    }),
  progress: (color: ColorName, isRebrand: boolean) => [
    css({
      height: 'inherit',
      borderRadius: 'inherit',
      position: 'absolute',
      top: 0,
      left: 0,
      maxWidth: '100%',
      transition: `all ${Animation.transitionDuration} ease-in-out`,
      backgroundColor: Colors[color],
    }),
    isRebrand && V5Overrides.progress(color),
  ],
});

/**
 * Basic progress component. Shows how full something is.
 */
export const ProgressBar = ({ max = 1, min = 0, ...props }: Props) => {
  const value = clamp(props.value, min, max);
  const range = max - min;
  const decimal = (value - min) / range;
  const width = `${decimal * 100}%`;
  const { isRebrand } = useTheme();
  const { color = isRebrand ? 'blue' : 'ocean' } = props;
  return (
    <div css={Styles.root(isRebrand)}>
      <div
        css={Styles.progress(color, isRebrand)}
        data-testid="progress"
        style={{
          width,
        }}
      />
    </div>
  );
};
